import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from './api';
import { forgetVerifiedKioskSession } from './offlineKioskAccess';
import './StripeConnectSettings.css';

const INVALID_TARGET = 'Ce lien de configuration Stripe est invalide. Utilisez le lien reçu pour votre établissement.';
const MISMATCH_TARGET = 'Ce lien concerne un autre établissement. Connectez-vous avec son identifiant administrateur.';

// undefined preserves the legacy entrypoint; null must never become an absent target.
export function stripeSetupAccountFromSearch(search) {
  const parameters = new URLSearchParams(search);
  const values = parameters.getAll('stripe_setup_account');
  if ([...parameters.keys()].some(key => key.startsWith('stripe_setup_account['))) return null;
  if (!values.length) return undefined;
  if (values.length !== 1 || !/^[1-9][0-9]*$/.test(values[0])) return null;
  const value = Number(values[0]);
  return Number.isSafeInteger(value) ? value : null;
}

export function trustedStripeUrl(value, kind = 'onboarding') {
  try {
    const url = new URL(value);
    const hosts = kind === 'dashboard' ? ['dashboard.stripe.com'] : ['connect.stripe.com', 'accounts.stripe.com'];
    return url.protocol === 'https:' && hosts.includes(url.hostname) && !url.port && !url.username && !url.password
      ? url.href : '';
  } catch { return ''; }
}

export function stripeStatusLabel(account) {
  if (!account?.configured) return 'Configuration de la plateforme en attente';
  if (account.status === 'error') return 'Vérification temporairement indisponible';
  if (account.status === 'disabled') return 'Compte restreint';
  if (!account.connected) return 'Compte Stripe à connecter';
  if (account.status === 'ready' && account.chargesEnabled && account.payoutsEnabled) {
    return account.mode === 'test' ? 'Compte prêt en mode test' : 'Paiements en ligne activés';
  }
  if (account.status === 'onboarding' || !account.detailsSubmitted) return 'Création du compte à compléter';
  return 'Vérification Stripe en cours';
}

function requestError(response, payload) {
  if (payload?.code === 'STRIPE_ACCOUNT_TARGET_INVALID' || payload?.error === 'STRIPE_ACCOUNT_TARGET_INVALID') return INVALID_TARGET;
  if (payload?.code === 'STRIPE_ACCOUNT_MISMATCH' || payload?.error === 'STRIPE_ACCOUNT_MISMATCH') return MISMATCH_TARGET;
  return [401, 403].includes(response.status)
    ? 'Cet accès est réservé à l’administrateur de l’établissement.'
    : 'Le statut Stripe est temporairement indisponible. Réessayez dans quelques instants.';
}

const openStripe = (url) => window.location.assign(url);

function ScopedStripeConnectSettings({ request, navigateExternal = openStripe, navigate, expectedAccountId }) {
  const invalidTarget = expectedAccountId !== undefined && (!Number.isSafeInteger(expectedAccountId) || expectedAccountId <= 0);
  const [account, setAccount] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [targetError, setTargetError] = useState('');
  const [changingAccount, setChangingAccount] = useState(false);
  const mounted = useRef(false);
  const generation = useRef(0);
  const submitting = useRef(false);

  const refresh = useCallback(async () => {
    const version = ++generation.current;
    setLoading(true); setError(''); setAccount(null); setConsent(false); setTargetError('');
    if (invalidTarget) {
      setError(INVALID_TARGET); setTargetError(INVALID_TARGET); setLoading(false);
      return;
    }
    try {
      const query = expectedAccountId === undefined ? '' : `?expectedAccountId=${expectedAccountId}`;
      const response = await request(`${API_URL}/api/admin/payments/stripe${query}`);
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw Error(requestError(response, payload));
      if (typeof payload?.configured !== 'boolean' || typeof payload?.connected !== 'boolean') {
        throw Error('Le statut Stripe reçu est incomplet. Réessayez dans quelques instants.');
      }
      if (mounted.current && version === generation.current) setAccount(payload);
    } catch (failure) {
      if (mounted.current && version === generation.current) {
        if ([INVALID_TARGET, MISMATCH_TARGET].includes(failure.message)) setTargetError(failure.message);
        setError([INVALID_TARGET, MISMATCH_TARGET].includes(failure.message) || failure.message.startsWith('Cet accès') || failure.message.startsWith('Le statut Stripe')
          ? failure.message : 'Impossible de joindre le service de paiement. Réessayez dans quelques instants.');
      }
    } finally {
      if (mounted.current && version === generation.current) setLoading(false);
    }
  }, [request, expectedAccountId, invalidTarget]);

  useEffect(() => {
    mounted.current = true;
    refresh();
    return () => { mounted.current = false; generation.current += 1; };
  }, [refresh]);

  async function connect(event) {
    event.preventDefault();
    if (!consent || !account?.configured || loading || submitting.current || changingAccount || targetError || invalidTarget || account.status === 'error') return;
    submitting.current = true; setConnecting(true); setError('');
    const version = generation.current;
    try {
      const response = await request(`${API_URL}/api/admin/payments/stripe/onboarding`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent: true, ...(expectedAccountId === undefined ? {} : { expectedAccountId }) }),
      });
      const payload = await response.json().catch(() => null);
      if (!mounted.current || version !== generation.current) return;
      if (!response.ok) {
        const message = requestError(response, payload);
        if ([401, 403].includes(response.status) || [INVALID_TARGET, MISMATCH_TARGET].includes(message)) { setAccount(null); setConsent(false); }
        if ([INVALID_TARGET, MISMATCH_TARGET].includes(message)) setTargetError(message);
        throw Error(message);
      }
      const url = trustedStripeUrl(payload?.url);
      if (!url) throw Error('Le lien Stripe reçu est invalide. Aucun site externe n’a été ouvert.');
      navigateExternal(url);
    } catch (failure) {
      if (mounted.current && version === generation.current) {
        setError([INVALID_TARGET, MISMATCH_TARGET].includes(failure.message) || failure.message.startsWith('Cet accès') || failure.message.startsWith('Le statut Stripe') || failure.message.startsWith('Le lien Stripe')
          ? failure.message : 'La connexion à Stripe n’a pas pu être lancée. Réessayez.');
      }
    } finally {
      if (mounted.current && version === generation.current) { submitting.current = false; setConnecting(false); }
    }
  }

  async function changeAccount() {
    if (submitting.current || changingAccount || targetError !== MISMATCH_TARGET || invalidTarget || expectedAccountId === undefined) return;
    submitting.current = true; setChangingAccount(true); setError(MISMATCH_TARGET);
    const version = generation.current;
    try {
      const response = await request(`${API_URL}/api/admin/logout`, { method: 'POST' });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.success !== true) throw Error('LOGOUT_FAILED');
      if (!mounted.current || version !== generation.current) return;
      forgetVerifiedKioskSession();
      localStorage.removeItem('authToken'); localStorage.removeItem('kioskAccount');
      navigate('/admin', { replace: true, state: { from: { pathname: '/admin/dashboard', search: `?tab=payments&stripe_setup_account=${expectedAccountId}` } } });
    } catch {
      if (mounted.current && version === generation.current) setError('La déconnexion n’a pas pu être confirmée. Réessayez pour changer de compte.');
    } finally {
      if (mounted.current && version === generation.current) { submitting.current = false; setChangingAccount(false); }
    }
  }

  const dashboardUrl = account?.connected ? trustedStripeUrl(account.dashboardUrl, 'dashboard') : '';
  const ready = account?.status === 'ready' && account.chargesEnabled && account.payoutsEnabled;
  const canConnect = account?.configured && account.status !== 'error' && !ready && !loading && !targetError && !invalidTarget;

  return <section className="admin-panel stripe-connect" aria-labelledby="stripe-connect-title">
    <div className="admin-panel-head"><span className="admin-panel-kicker">Paiements en ligne</span><h2 id="stripe-connect-title">Votre compte Stripe</h2></div>
    <p>Encaissez les commandes de la suite BERESTO sur votre propre compte Stripe. Les fonds sont versés sur le compte bancaire de votre établissement.</p>
    <p>Stripe recueille directement vos informations d’identité et votre IBAN. Vous conservez l’accès à votre tableau de bord Stripe complet pour gérer paiements, virements et litiges.</p>
    <div className="stripe-connect-status" role="status" aria-live="polite">
      <strong>{loading ? 'Vérification du compte Stripe…' : account ? stripeStatusLabel(account) : 'Statut non disponible'}</strong>
      {account?.mode === 'test' ? <p>Mode test : aucun paiement réel ne peut être encaissé avec cette configuration.</p> : null}
      {account && !account.configured ? <p>BERESTO doit terminer la configuration de sa plateforme Stripe. La création de votre compte sera disponible ensuite.</p> : null}
      {account?.connected ? <dl className="stripe-connect-details">
        <div><dt>Paiements</dt><dd>{account.chargesEnabled ? 'Autorisés' : 'En attente'}</dd></div>
        <div><dt>Virements bancaires</dt><dd>{account.payoutsEnabled ? 'Autorisés' : 'En attente'}</dd></div>
        <div><dt>Informations du compte</dt><dd>{account.detailsSubmitted ? 'Transmises à Stripe' : 'À compléter'}</dd></div>
      </dl> : null}
      {account?.configured && account.connected && account.status === 'disabled' ? <p>Consultez Stripe pour connaître les informations ou actions nécessaires.</p> : null}
    </div>
    {error ? <p className="stripe-connect-error" role="alert">{error}</p> : null}
    {targetError === MISMATCH_TARGET && expectedAccountId !== undefined && !invalidTarget ? <button className="admin-button" type="button" onClick={changeAccount} disabled={changingAccount}>{changingAccount ? 'Déconnexion…' : 'Changer de compte'}</button> : null}
    {canConnect ? <form onSubmit={connect} className="stripe-connect-form">
      <label className="stripe-connect-consent"><input type="checkbox" checked={consent} disabled={connecting} onChange={(event) => setConsent(event.target.checked)} /><span>J’autorise BERESTO, plateforme de SASU BEQUANIM, à accéder à mon compte Stripe pour gérer les paiements de mes applications BERESTO. Je confirmerai cette connexion et les conditions applicables sur Stripe.</span></label>
      <button className="admin-button" type="submit" disabled={!consent || connecting}>{connecting ? 'Ouverture de Stripe…' : account.connected ? 'Compléter mon compte Stripe' : 'Créer mon compte Stripe'}</button>
    </form> : null}
    <div className="stripe-connect-actions">
      <button className="admin-button admin-button-secondary" type="button" onClick={refresh} disabled={loading || connecting || changingAccount || invalidTarget}>Actualiser le statut</button>
      {dashboardUrl ? <a className="admin-button admin-button-secondary" href={dashboardUrl} target="_blank" rel="noopener noreferrer">Ouvrir mon tableau de bord Stripe</a> : null}
    </div>
    <p className="stripe-connect-note">Après votre retour de Stripe, le statut est revérifié auprès du serveur. Revenir ici ne confirme pas à lui seul l’activation des paiements. Cette connexion concerne les paiements en ligne.</p>
  </section>;
}

export default function StripeConnectSettings({ scopeKey, expectedAccountId, ...props }) {
  return <ScopedStripeConnectSettings key={`${scopeKey}:stripe-target:${expectedAccountId === undefined ? 'absent' : expectedAccountId}`} expectedAccountId={expectedAccountId} {...props} />;
}
